import type { Capture } from './types';

/** The one status a snapshot page leads with (D42) */
export type Standing = 'pending' | 'completed' | 'failed' | 'cancelled' | 'deleted';

/**
 * What a snapshot's state means to a person: pending while its packages arrive, completed, failed when
 * its packages never all arrived in time (the service says so, and a pending one past its deadline already
 * is), cancelled, or deleted and recoverable. Trying again on a failed or cancelled snapshot cannot help: it
 * is made again in Packages.
 */
export class Condition {
	/** How long a pending snapshot may wait when the service does not say (`SNAPSHOTS_PENDING_SECONDS`' default) */
	static WINDOW = 3_600_000;

	/** The moment a pending snapshot can no longer complete */
	static deadline(capture: Capture): number {
		const expires = Date.parse(capture.expires ?? '');
		return Number.isFinite(expires) ? expires : Date.parse(capture.created) + Condition.WINDOW;
	}

	static of(capture: Capture, now = Date.now()): Standing {
		if (capture.state === 'deleted') return 'deleted';
		if (capture.state === 'active') return 'completed';
		if (capture.state === 'cancelled') return 'cancelled';
		if (capture.state === 'failed') return 'failed';
		return now >= Condition.deadline(capture) ? 'failed' : 'pending';
	}

	/** Whether it ended without completing: cancelled or failed */
	static ended(capture: Capture, now = Date.now()): boolean {
		const standing = Condition.of(capture, now);
		return standing === 'cancelled' || standing === 'failed';
	}

	/** The packages whose bytes never arrived */
	static missing(capture: Capture): string[] {
		return capture.packages.filter(entry => !entry.stored).map(entry => entry.path);
	}
}
